/**
 * Live telemetry panel - everything the app is measuring, as it measures it.
 *
 * Partly for the pleasure of watching it, partly because it is the fastest way
 * to tell whether a sensor is actually working: if the roughness index sits at
 * "--" the whole drive, motion permission was refused, and no amount of
 * staring at a trip log afterwards would have told you that.
 *
 * Updated straight from the navigation loop rather than on a timer, so the
 * numbers move exactly when the data does.
 */

import type { Fix, NavProgress } from '../core/types';
import type { UnitSystem } from '../core/settings';
import type { RoadTier } from '../nav/paceModel';
import { roughnessBand } from '../nav/roughness';
import { formatDistance, formatDuration, formatSpeed, speedUnitLabel } from '../core/format';
import { t } from '../core/i18n';

export interface LiveDataState {
  fix: Fix | null;
  progress: NavProgress | null;
  roughness: number | null;
  tier: RoadTier | null;
  /** Metres driven and samples stored so far this trip. */
  tripDistance: number;
  samples: number;
  movingSeconds: number;
  /** Gradient right here, percent, when the elevation profile is loaded. */
  gradeNow: number | null;
  signalsOnRoute: number | null;
  fleetCount: number;
  units: UnitSystem;
}

export class LiveDataPanel {
  readonly button: HTMLElement;
  private panel: HTMLElement;
  private get: () => LiveDataState;

  constructor(parent: HTMLElement, getState: () => LiveDataState) {
    this.get = getState;

    this.button = document.createElement('button');
    this.button.className = 'live-fab';
    this.button.hidden = true;
    this.button.setAttribute('aria-label', t('live.title'));
    this.button.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M2 13 H6 L9 5 L13 19 L16 11 L18.5 13 H22" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    parent.appendChild(this.button);
    this.button.addEventListener('click', () => this.toggle());

    this.panel = document.createElement('div');
    this.panel.className = 'live-panel';
    this.panel.hidden = true;
    parent.appendChild(this.panel);
    const touched = () => (this.touchedAt = Date.now());
    this.panel.addEventListener('touchstart', touched, { passive: true });
    this.panel.addEventListener('touchmove', touched, { passive: true });
    this.panel.addEventListener('scroll', touched, { passive: true, capture: true });
  }

  setVisible(visible: boolean): void {
    this.button.hidden = !visible;
    if (!visible) this.panel.hidden = true;
  }

  private toggle(): void {
    this.panel.hidden = !this.panel.hidden;
    if (!this.panel.hidden) this.render();
  }

  close(): void {
    this.panel.hidden = true;
  }

  /** Called on every GPS fix; cheap no-op while closed. */
  refresh(): void {
    if (!this.panel.hidden) this.render();
  }

  /**
   * Re-rendering wholesale resets the scroll position, and this runs on every
   * GPS fix - so reading anything below the fold was impossible: the sheet
   * snapped back to the top about once a second. Reported from the road as
   * "the route ahead scrolls to top automatically all the time."
   *
   * Two guards: keep the scroll offset across the swap, and hold off entirely
   * while a finger is on the panel, since replacing the DOM mid-flick kills
   * momentum scrolling even when the offset is restored.
   */
  private touchedAt = 0;

  private render(force = false): void {
    const scroller = this.panel.querySelector<HTMLElement>('.live-grid');
    if (!force && scroller && Date.now() - this.touchedAt < 2000) return;
    const keepTop = scroller?.scrollTop ?? 0;
    this.renderInner();
    const next = this.panel.querySelector<HTMLElement>('.live-grid');
    if (next && keepTop) next.scrollTop = keepTop;
  }

  private renderInner(): void {
    const s = this.get();
    const u = speedUnitLabel(s.units);
    const rough = s.roughness;

    const tiles: string[] = [
      tile(t('live.speed'), formatSpeed(s.fix?.speed ?? null, s.units), u, 'big'),
      tile(
        t('live.roughness'),
        rough == null ? '--' : String(rough),
        rough == null ? t('live.noSensor') : t(`live.rough.${roughnessBand(rough)}`),
        rough == null ? '' : `rough-${roughnessBand(rough)}`,
      ),
      tile(t('live.altitude'), fmt(s.fix?.altitude), 'm'),
      tile(t('live.accuracy'), fmt(s.fix?.accuracy), 'm', (s.fix?.accuracy ?? 99) > 25 ? 'warn' : ''),
      tile(t('live.heading'), s.fix?.heading == null ? '--' : `${Math.round(s.fix.heading)}`, '°'),
      tile(t('live.gradeNow'), s.gradeNow == null ? '--' : s.gradeNow.toFixed(1), '%'),
      tile(t('live.roadClass'), s.tier ? t(`live.tier.${s.tier}`) : '--', ''),
      tile(t('live.driven'), formatDistance(s.tripDistance, s.units), ''),
      tile(t('live.moving'), formatDuration(s.movingSeconds), ''),
      tile(t('live.samples'), String(s.samples), ''),
    ];

    if (s.progress) {
      tiles.push(
        tile(t('live.toTurn'), formatDistance(s.progress.distanceToManeuver, s.units), ''),
        tile(t('live.offRoute'), fmt(s.progress.snapped.deviation), 'm'),
      );
    }
    if (s.signalsOnRoute != null) tiles.push(tile(t('info.signals'), String(s.signalsOnRoute), ''));
    if (s.fleetCount > 0) tiles.push(tile(t('fleet.title'), String(s.fleetCount), ''));

    this.panel.innerHTML = `
      <div class="live-panel__head">
        <h3>${t('live.title')}</h3>
        <button class="icon-btn" data-act="close" aria-label="${t('preview.cancel')}">
          <svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
        </button>
      </div>
      <div class="live-grid">${tiles.join('')}</div>
      <div class="live-panel__foot">${t('live.note')}</div>`;

    this.panel.querySelector('[data-act=close]')!.addEventListener('click', () => this.close());
  }
}

function fmt(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '--' : String(Math.round(v));
}

function tile(label: string, value: string, unit: string, mod = ''): string {
  return `<div class="live-tile ${mod ? `live-tile--${mod}` : ''}">
    <span class="live-tile__label">${label}</span>
    <span class="live-tile__value">${value}${unit ? `<em>${unit}</em>` : ''}</span>
  </div>`;
}
